import { UseFieldArrayReturn, useFormContext } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { v4 as uuidv4 } from "uuid";
import { memo, useState, useMemo, useCallback } from "react";
import InfoTooltip from "@/components/info-tooltip";
import { useAgentForm } from "@/contexts/AgentFormContext";
import { ChevronDown, ChevronUp, Plus } from "lucide-react";
import { Trash2 } from "lucide-react";
import ConfirmationDialog from "./confirmation-dialog";
import { MAX_FAQS } from "@/schemas/forms/patch";
import { motion, AnimatePresence } from "framer-motion";

interface FaqsField {
  id: string;
  question: string;
  answer: string;
}

interface FaqsProps {
  fields: FaqsField[];
  remove: UseFieldArrayReturn<FaqsField>["remove"];
  prepend: UseFieldArrayReturn<FaqsField>["prepend"];
}

export const LOCAL_ID_PREFIX = "local-";
export const MAX_QUESTION_LENGTH = 150;
export const MAX_ANSWER_LENGTH = 600;

const FaqItem = memo(
  ({
    index,
    field,
    remove,
    isExpanded,
    onToggle,
  }: {
    index: number;
    field: FaqsField;
    remove: (index: number) => void;
    isExpanded: boolean;
    onToggle: (value: boolean) => void;
  }) => {
    const { control } = useFormContext();

    return (
      <div
        key={field.id}
        className="overflow-hidden rounded-lg border border-gray-200 bg-[#fafafa]"
      >
        <div className="px-3 py-2" onClick={() => onToggle(!isExpanded)}>
          <div className="flex flex-row items-center justify-between gap-4">
            <FormField
              control={control}
              name={`faqs.${index}.question`}
              render={({ field: questionField }) => (
                <FormItem className="flex flex-grow-[3]">
                  <FormControl className="flex-grow">
                    {!isExpanded ? (
                      <div className="text-sm font-medium">
                        {questionField.value?.trim() || ""}
                      </div>
                    ) : (
                      <div
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Input
                          placeholder="Question"
                          {...questionField}
                          value={questionField.value || ""}
                          className="py-1.5"
                          maxLength={MAX_QUESTION_LENGTH}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isExpanded && (
              <div className="flex cursor-pointer items-end justify-center">
                <div className="text-xs text-gray-500 select-none">
                  collapse
                </div>
              </div>
            )}

            <div className="flex flex-row items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                className="p-1 px-2"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(index);
                }}
              >
                <Trash2 className="h-3.5 w-3.5 text-gray-500" />
              </Button>
              <motion.div
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ type: "spring", stiffness: 120, damping: 15 }}
              >
                <ChevronDown size={14} color="#6b7280" />
              </motion.div>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 20,
                duration: 0.2,
              }}
            >
              <div className="space-y-2 px-3 pb-3">
                <FormField
                  control={control}
                  name={`faqs.${index}.answer`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Textarea
                          placeholder="Answer"
                          {...field}
                          value={field.value || ""}
                          className="min-h-24 resize-y"
                          maxLength={MAX_ANSWER_LENGTH}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  },
);

// Main FAQs component
const Faqs = ({ fields, remove, prepend }: FaqsProps) => {
  const { agentForm } = useAgentForm();
  const { dirtyFields } = agentForm.formState;
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [expandedStates, setExpandedStates] = useState<boolean[]>(
    fields.map((field) => field.question === "" && field.answer === ""),
  );

  const hasReachedMaxFaqs = fields.length >= MAX_FAQS;

  const allCollapsed = useMemo(
    () => expandedStates.every((state) => !state),
    [expandedStates],
  );

  const handleToggleAll = useCallback(() => {
    const newState = allCollapsed;
    setExpandedStates(new Array(fields.length).fill(newState));
  }, [allCollapsed, fields.length]);

  const handleToggleItem = useCallback((index: number, value: boolean) => {
    setExpandedStates((prev) => {
      const newStates = [...prev];
      newStates[index] = value;
      return newStates;
    });
  }, []);

  return (
    <div className="space-y-2">
      <div className="gap-2">
        <div className="mb-2 flex flex-row items-center justify-between">
          <div>
            <div className="flex flex-row items-center gap-1">
              <div className="text-sm font-medium">FAQs</div>
              {dirtyFields.faqs && (
                <div className="text-xs font-medium text-blue-500">
                  (edited)
                </div>
              )}
              <InfoTooltip content="Answer common questions your customers might be asking. Please note that links are not supported." />
            </div>
          </div>
          {fields.length > 0 && (
            <Button
              type="button"
              variant="outline"
              className="flex flex-row items-center gap-1 py-1.5"
              onClick={handleToggleAll}
            >
              {allCollapsed ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronUp className="h-3.5 w-3.5" />
              )}
              <div className="text-sm">
                {allCollapsed ? "Expand all" : "Collapse all"}
              </div>
            </Button>
          )}
        </div>
        <Button
          type="button"
          variant="secondary"
          className="flex w-full flex-row items-center gap-1 py-1.5"
          disabled={hasReachedMaxFaqs}
          onClick={() => {
            prepend({
              id: LOCAL_ID_PREFIX + uuidv4(),
              question: "",
              answer: "",
            });
            setExpandedStates((prev) => [true, ...prev]);
          }}
        >
          <Plus className="h-3.5 w-3.5" />
          Add FAQ
        </Button>
        {hasReachedMaxFaqs && (
          <div className="mb-3 text-center text-sm text-gray-500">
            You can have a maximum of {MAX_FAQS} FAQs
          </div>
        )}
      </div>

      {fields.map((field, index) => (
        <FaqItem
          key={field.id}
          index={index}
          field={field}
          remove={(idx) => {
            setDeleteIndex(idx);
            setIsDeleteModalVisible(true);
          }}
          isExpanded={expandedStates[index]}
          onToggle={(value) => handleToggleItem(index, value)}
        />
      ))}

      <ConfirmationDialog
        visible={isDeleteModalVisible}
        title="Delete FAQ"
        message="Are you sure you want to delete this FAQ?"
        confirmText="Delete"
        onConfirm={() => {
          if (deleteIndex !== null) {
            remove(deleteIndex);
            setExpandedStates((prev) =>
              prev.filter((_, i) => i !== deleteIndex),
            );
          }
          setIsDeleteModalVisible(false);
        }}
        onCancel={() => setIsDeleteModalVisible(false)}
        redConfirm={true}
      />
    </div>
  );
};

export default memo(Faqs);
